import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button, Container } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSnackbar } from "notistack";

import StickyFooter from "../components/StickyFooter";
import CardView from "../components/CardView";
import EmptyTransactionWidget from "../components/widgets/EmptyTransactionWidget";
import AccountStatisticsWidget from "../components/widgets/AccountStatisticsWidget";
import QuickActionsWidget from "../components/widgets/QuickActionswidgets";
import TotalBalance from "../components/TotalBalance";
import Header from "../components/Header";
import { useThemeContext } from "../pages/context/ThemeContext";

// API
import { getUser } from "../utils/api";

export default function Dashboard() {
  const { mode } = useThemeContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);

  useEffect(() => {
    getUser()
      .then((u) => setUser(u))
      .catch(() => {
        enqueueSnackbar("⚠️ Unable to load user details.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      });
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor:
          mode === "dark"
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflowX: "hidden", // ✅ prevent horizontal scroll
      }}
    >
      <Container
        maxWidth="lg"
        disableGutters
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          px: { xs: 1.5, sm: 2, md: 4 },
        }}
      >
        {/* 🔹 Header */}
        <Header mode={mode} onMenuClick={() => setMenuOpen(true)} />
        <Box mt={{ xs: 2, md: 3 }} /> {/* spacing before banner */}

        {/* 🔹 KYC Banner */}
        {user && user.kyc_status !== "approved" && (
          <Grid
            container
            spacing={2}
            p={2}
            mt={1}
            borderRadius={3}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              boxSizing: "border-box",
              bgcolor:
                mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(255, 243, 224, 0.7)",
              boxShadow:
                mode === "dark"
                  ? "0 4px 12px rgba(0,0,0,0.5)"
                  : "0 4px 12px rgba(0,0,0,0.1)",
              backdropFilter: "blur(8px)",
              borderLeft: "4px solid #f59e0b",
            }}
          >
            {/* Left side */}
            <Grid
              item
              xs={12}
              md={8}
              display="flex"
              alignItems="center"
              sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
            >
              <WarningAmberIcon
                color="warning"
                sx={{ fontSize: 36, mr: 2, flexShrink: 0 }}
              />
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ whiteSpace: { xs: "normal", md: "nowrap" } }}
                >
                  KYC Verification Pending
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: "break-word" }}
                >
                  Your account verification is not yet completed. Please
                  complete your KYC in <strong>Account Settings</strong> to
                  unlock all features.
                </Typography>
              </Box>
            </Grid>

            {/* Right side */}
            <Grid item xs={12} md="auto">
              <Button
                variant="contained"
                color="warning"
                fullWidth
                sx={{
                  borderRadius: 2,
                  minWidth: { xs: "100%", md: 150 },
                  mt: { xs: 2, md: 0 },
                }}
               onClick={() => navigate("/user/kyc-verification")} // ⬅️ redirect
                >
                Complete Now
              </Button>
            </Grid>
          </Grid>
        )}

        
        {/* 🔹 Main Content */}
<Box mt={{ xs: 2, md: 4 }} flex={1} width="100%">
  <Grid container spacing={2}>
    {/* Left column */}
    <Grid item xs={12} md={8}>
      <Box mb={2}>
        <TotalBalance sx={{ width: "100%" }} />
      </Box>

      {/* ✅ Four widgets in one row on desktop */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <AccountStatisticsWidget />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CardView />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionsWidget />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EmptyTransactionWidget />
        </Grid>
      </Grid>
    </Grid>

    {/* Right column */}
    <Grid item xs={12} md={4}>
      {/* Add your TransactionWidget or others here */}
    </Grid>
  </Grid>
</Box>


      </Container>

      {/* 🔹 Sticky Footer */}
      <StickyFooter />
    </Box>
  );
}